import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TrendingUp, Users, Globe, FileDown, Calendar, BarChart3 } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import EmployeeReportingTable from "./EmployeeReportingTable";
import { useTranslation } from "react-i18next";
import { getCountryDisplayName } from "@/utils/countryTranslations";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface ReportData {
  currentlyRemote: number;
  totalRemoteRequests: number;
  countriesData: Array<{
    name: string;
    count: number;
    days: number;
  }>;
  monthlyData: Array<{
    month: string;
    requests: number;
    employees: number;
  }>;
  statusDistribution: Array<{
    name: string;
    value: number;
    color: string;
  }>;
}

interface ReportsTabProps {
  company: Company | null;
}

const ReportsTab = ({ company }: ReportsTabProps) => {
  const { t, i18n } = useTranslation();
  const [reportData, setReportData] = useState<ReportData>({
    currentlyRemote: 0,
    totalRemoteRequests: 0,
    countriesData: [],
    monthlyData: [],
    statusDistribution: []
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    if (company) {
      fetchReportData();
    }
  }, [company]);

  const fetchReportData = async () => {
    if (!company) return;

    try {
      // Fetch all remote work requests for the company using secure function
      const { data: allRequests } = await supabase
        .rpc('get_company_remote_work_requests');

      // Currently remote (approved requests for today)
      const today = new Date().toISOString().split('T')[0];
      const currentlyRemote = allRequests?.filter(
        req => req.status === 'approved' && req.start_date <= today && req.end_date >= today
      ).length || 0;

      // Status distribution
      const statusCounts = allRequests?.reduce((acc, req) => {
        acc[req.status] = (acc[req.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const statusDistribution = [
        { name: t("dashboard.reports.statusLabels.approved", "Approved"), value: statusCounts.approved || 0, color: '#10B981' },
        { name: t("dashboard.reports.statusLabels.pending", "Pending"), value: statusCounts.pending || 0, color: '#F59E0B' },
        { name: t("dashboard.reports.statusLabels.rejected", "Rejected"), value: statusCounts.rejected || 0, color: '#EF4444' }
      ];

      // Monthly data for the last 6 months
      const monthlyData = [];
      const locale = i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'es' ? 'es-ES' : 'en-US';
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthKey = date.toISOString().substring(0, 7);
        const monthLabel = date.toLocaleDateString(locale, { month: 'short', year: 'numeric' });
        
        const monthRequests = allRequests?.filter(
          req => req.created_at.startsWith(monthKey)
        ) || [];
        
        const uniqueEmployees = new Set(monthRequests.map(req => req.employee_id)).size;
        
        monthlyData.push({
          month: monthLabel,
          requests: monthRequests.length,
          employees: uniqueEmployees
        });
      }

      // Fetch destinations for countries data
      const { data: destinations } = await supabase
        .from('destinations')
        .select('*');

      // Mock countries data (in real app, you'd map employees to destinations)
      const countriesData = [
        { name: 'France', count: Math.floor((currentlyRemote * 0.6)), days: Math.floor(Math.random() * 30) + 10 },
        { name: 'Spain', count: Math.floor((currentlyRemote * 0.25)), days: Math.floor(Math.random() * 20) + 5 },
        { name: 'Portugal', count: Math.floor((currentlyRemote * 0.15)), days: Math.floor(Math.random() * 15) + 3 },
      ].filter(country => country.count > 0);

      setReportData({
        currentlyRemote,
        totalRemoteRequests: allRequests?.length || 0,
        countriesData,
        monthlyData,
        statusDistribution: statusDistribution.filter(s => s.value > 0)
      });

    } catch (error) {
      console.error('Error fetching report data:', error);
      toast({
        variant: "destructive",
        title: t("common.error", "Error"),
        description: t("dashboard.governance.toast.cannotLoadData", "Unable to load data")
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    // Generate CSV data
    const csvData = [
      [t("common.metric", "Metric"), t("common.value", "Value")],
      [t("dashboard.reports.currentlyRemote", "Currently remote"), reportData.currentlyRemote.toString()],
      [t("dashboard.reports.totalRequests", "Total requests"), reportData.totalRemoteRequests.toString()],
      ['', ''],
      [t("dashboard.reports.countryBreakdown", "Country Breakdown"), ''],
      ...reportData.countriesData.map(country => [
        getCountryDisplayName(country.name, i18n.language), 
        `${country.count} ${t("dashboard.reports.employees", "employees")}, ${country.days} ${t("dashboard.reports.days", "days")}`
      ]),
      ['', ''],
      [t("dashboard.reports.requestDistribution", "Request Distribution"), ''],
      ...reportData.statusDistribution.map(status => [status.name, status.value.toString()])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `remote-work-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    toast({
      title: t("dashboard.reports.exportSuccess", "Export successful"),
      description: t("dashboard.reports.reportExported", "The report has been exported to CSV")
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-8 bg-muted rounded w-1/4"></div>
                <div className="h-32 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                {t("dashboard.reports.title", "Remote Work Report")}
              </CardTitle>
              <CardDescription>
                {t("dashboard.reports.subtitle", "Complete analysis of your company's remote work activity")}
              </CardDescription>
            </div>
            <Button onClick={handleExportCSV} variant="outline">
              <FileDown className="h-4 w-4 mr-2" />
              {t("dashboard.reports.exportCSV", "Export CSV")}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.reports.currentlyRemote", "Currently remote")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {reportData.currentlyRemote}
                </p>
              </div>
              <Users className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.reports.totalRequests", "Total requests")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {reportData.totalRemoteRequests}
                </p>
              </div>
              <FileDown className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  {t("dashboard.reports.activeCountries", "Active countries")}
                </p>
                <p className="text-3xl font-bold text-foreground">
                  {reportData.countriesData.length}
                </p>
              </div>
              <Globe className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              {t("dashboard.reports.requestDistribution", "Request Distribution")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={reportData.statusDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {reportData.statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Evolution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              {t("dashboard.reports.monthlyEvolution", "Monthly Evolution")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reportData.monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="requests" fill="hsl(var(--primary))" name={t("dashboard.requests.title", "Requests")} />
                  <Bar dataKey="employees" fill="hsl(var(--secondary))" name={t("dashboard.employees.title", "Employees")} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Countries Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            {t("dashboard.reports.countryBreakdown", "Country Breakdown")}
          </CardTitle>
          <CardDescription>
            {t("dashboard.reports.countryBreakdownDesc", "Number of employees and remote work days by destination")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {reportData.countriesData.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {t("dashboard.reports.noActivity", "No international remote work activity")}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reportData.countriesData.map((country, index) => (
                <div key={country.name} className="flex justify-between items-center p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                      index === 0 ? 'bg-blue-500' : index === 1 ? 'bg-green-500' : 'bg-purple-500'
                    }`}>
                      {index + 1}
                    </div>
                    <div>
                      <p className="font-medium">{getCountryDisplayName(country.name, i18n.language)}</p>
                      <p className="text-sm text-muted-foreground">
                        {country.count} {country.count > 1 ? t("dashboard.reports.employees", "employees") : t("dashboard.reports.employee", "employee")}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary">
                      {country.days} {t("dashboard.reports.days", "days")}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Reporting Table */}
      <EmployeeReportingTable company={company} />
    </div>
  );
};

export default ReportsTab;
